import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../assets/images/normal_logo.png";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import GetUsers from "../hooks/getUsers";
import { useAuth } from "../services/authProvider";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { selectOrganization, setRole } from "../services/auth_service";
import { setAccessToken } from "../utils/authFetch";
import { jwtDecode } from "jwt-decode";
import { baseUrl } from "../utils/requestUrl";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const Login = (props) => {
  const { login, updateToken } = useAuth();
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState(null);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleLogin = () => {
    login.mutate({ email, password });
  };

  const isProfileComplete = (profile) =>
    profile?.sex &&
    profile?.dateOfBirth &&
    profile?.languagePreference &&
    (profile?.emails?.length || profile?.email) &&
    profile?.phones?.length &&
    profile?.mailingAddresses?.length;

  const handleAutoSelectFlow = async (accessToken) => {
    setIsAutoSelecting(true);
    try {
      setAccessToken(accessToken);

      const userInfo = jwtDecode(accessToken);

      // Auto-select first organization
      const firstOrg = userInfo.organizations?.[0];
      if (!firstOrg) throw new Error("No organization found");

      const orgResponse = await selectOrganization(firstOrg.id);
      updateToken(orgResponse.data.token);

      // Decode updated token to get roles
      const updatedUserInfo = jwtDecode(orgResponse.data.token);
      const activeOrg = updatedUserInfo.organizations?.find((o) => o.active);

      // Find first valid role (exclude USER role from ORGANIZATION type)
      const orgRoles = (updatedUserInfo.roles || []).filter((role) => {
        if (role.organizationId !== activeOrg?.id) return false;
        if (role.type === "ORGANIZATION" && role.role === "USER") return false;
        return true;
      });

      const firstRole = orgRoles[0];
      if (!firstRole) throw new Error("No role found");

      const roleId =
        firstRole.type === "CLINICAL_LOCATION"
          ? firstRole.clinicalLocationId
          : firstRole.organizationId;

      const roleResponse = await setRole({
        roleType: firstRole.type,
        roleName: firstRole.role,
        id: roleId,
      });
      updateToken(roleResponse.data.token);

      // Fetch profile to check completeness rather than relying on flag alone
      const profileResponse = await fetch(`${baseUrl}/user/profile`, {
        headers: { Authorization: `Bearer ${roleResponse.data.token}` },
      });
      const profileData = await profileResponse.json();
      const profile = profileData?.data;

      if (isProfileComplete(profile)) {
        // Profile already complete — set flag and send to manual org/role selection
        const userId = profile?.id;
        if (userId) localStorage.setItem(`profileComplete_${userId}`, "true");
        props.history.push("/user/organization");
      } else {
        props.history.push("/hcp/first_login_profile/personal_details");
      }
    } catch (error) {
      console.error("Auto-select flow failed:", error);
      props.history.push("/hcp/first_login_profile/personal_details");
    } finally {
      setIsAutoSelecting(false);
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.id;
        const flagSet = userId
          ? localStorage.getItem(`profileComplete_${userId}`) === "true"
          : false;

        if (flagSet) {
          // Fast path: flag confirmed, skip profile fetch
          props.history.push("/user/organization");
          return;
        }
      } catch {
        // fall through to auto-select flow
      }
      // Flag absent or unreadable — auto-select org/role then fetch profile to verify
      handleAutoSelectFlow(accessToken);
      return;
    }
    const status = login?.data?.status;
    let errorMessage = null;

    if (status === "400 BAD_REQUEST" || status?.startsWith("400")) {
      errorMessage = "Invalid credentials. Please check your email and password.";
    } else if (status === "401 UNAUTHORIZED" || status?.startsWith("401")) {
      errorMessage = "You are not authorized to access this application.";
    } else if (status?.startsWith("5")) {
      errorMessage = "A server error occurred. Please try again later.";
    }

    if (errorMessage) {
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    }
  }, [login.isPending]);

  return (
    <div className="main">
      <div className="col-lg-4">
        <div className="card border-0 shadow sm">
          <div className="card-body px-5">
            <div className="text-center">
              <img
                src={Logo}
                className="img-fluid py-4"
                width={185}
                height={80}
                alt=""
              />
            </div>
            <form className="mt-3">
              <div className="form-group">
                <input
                  type="email"
                  className="form-control text-dark outline-none"
                  placeholder="Email address"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="form-group py-1">
                <div
                  className="d-flex align-items-center form-control"
                  style={{ borderRadius: "8px", padding: "0 12px" }}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    className="border-0 outline-none flex-grow-1 text-dark"
                    style={{ background: "transparent", height: "38px" }}
                    placeholder="Password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span
                    className="cursor-pointer"
                    style={{ color: "#aaa" }}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </span>
                </div>
              </div>
              <a
                href="#"
                className="primary_text font_light small-text"
                data-toggle="modal"
                data-target="#forgotPassword"
              >
                Forgot Password ?
              </a>
              <ForgotPasswordModal />
              <button
                type="button"
                onClick={handleLogin}
                className="btn btn-primary btn-block my-4 py-2"
                disabled={login.isPending || isAutoSelecting}
              >
                {(login.isPending || isAutoSelecting) && (
                  <div
                    className="spinner-border text_primary"
                    style={{ width: "1.3rem", height: "1.3rem" }}
                    role="status"
                  ></div>
                )}
                {!login.isPending && !isAutoSelecting && "Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
